import store from 'app/store';
import {
  ItemType,
  LevelType,
  SizingCalculatorAttributesType,
  SpaceType,
  ValidSizingCalculatorAttributesType
} from 'data/types';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { getItem } from 'utils/data';
import { allRequiredAttributesCompleted } from './attributes';
import { isValidSizingItem } from './sizing';

const sweetalert = withReactContent(Swal);

function checkSizingAttributes(
  space: SpaceType,
  focus?: string
): string | null {
  if (focus) {
    return null;
  }

  const hasSizingAttributes =
    space.sizingAttributes &&
    Object.entries(space.sizingAttributes).some(
      ([, value]) => value.length > 0
    );

  return hasSizingAttributes ? null : 'Space has no floors, ceilings, or walls';
}

function checkAdvancedModeValidation(
  space: SpaceType,
  easyMode: boolean
): { error: string | null; warning: string | null } {
  if (easyMode) {
    return { error: null, warning: null };
  }

  let warning: string | null = null;
  let error: string | null = null;

  const hasExtremeAspectRatio =
    space.width &&
    space.length &&
    (space.length > space.width * 2 || space.width > space.length * 2);

  if (hasExtremeAspectRatio) {
    warning = 'Space may need additional air handlers';
  }

  if (!allRequiredAttributesCompleted(space.attributes)) {
    error = 'Space is missing required attributes';
  } else if (space.BTUs.cooling === 0 && space.BTUs.heating === 0) {
    error = 'Space has no BTU values';
  }

  return { error, warning };
}

function checkSiblingValidation(
  space: SpaceType,
  siblings: SpaceType[],
  item: ItemType | undefined,
  focus?: string
): string | null {
  if (focus !== 'sections' && focus !== undefined) {
    return null;
  }

  if (item?.canBeAlone === false && siblings.length === 0) {
    return 'Space cannot be by itself';
  }

  const hasSiblingWithConditionedSpace = siblings.some(
    (sibling) => getItem(sibling.itemId)?.canBeAlone
  );

  if (
    item?.canBeAlone === false &&
    siblings.length > 0 &&
    !hasSiblingWithConditionedSpace
  ) {
    return 'Space must be paired with at least one conditioned space';
  }

  if (item?.mustBeAlone && siblings.length > 0) {
    return 'Space must be by itself';
  }

  return null;
}

export function validateSpace(
  space: SpaceType,
  siblings: SpaceType[],
  focus?: 'levels' | 'sections' | 'spaces'
): SpaceType {
  if (!space.conditioned) {
    return {
      ...space,
      valid: true,
      errorMessage: undefined,
      warningMessage: undefined
    };
  }

  const { easyMode } = store.getState().ui;
  const item = getItem(space.itemId);

  const sizingError = checkSizingAttributes(space, focus);
  const { error: advancedError, warning } = checkAdvancedModeValidation(
    space,
    easyMode
  );
  const siblingError = checkSiblingValidation(space, siblings, item, focus);

  const errorMessage = sizingError ?? advancedError ?? siblingError ?? null;
  const valid = errorMessage === null;

  return {
    ...space,
    valid,
    errorMessage,
    warningMessage: warning
  };
}

export function validateLevel(level: LevelType): LevelType {
  const newLevel = {
    ...level,
    sections: level.sections.map((section) => ({
      ...section,
      spaces: section.spaces.map((space) =>
        validateSpace(
          space,
          section.spaces.filter((sp) => sp.uniqueId !== space.uniqueId),
          undefined
        )
      )
    }))
  };

  let valid = true;
  const warningMessage: string | null = null;
  const errorMessage: string | null = null;

  if (
    newLevel.sections.some((section) =>
      section.spaces.some((space) => space.valid === false)
    )
  ) {
    valid = false;
  }

  return {
    ...newLevel,
    valid,
    errorMessage,
    warningMessage
  };
}

function revalidateNestedItems<T extends { valid?: boolean }>(
  items: T[] | undefined,
  type: keyof ValidSizingCalculatorAttributesType
): T[] | undefined {
  if (!items) return items;
  return items.map((item) => ({
    ...item,
    valid: isValidSizingItem(type, item as any)
  }));
}

export function validateSizingAttributes(
  sizingAttributes: SizingCalculatorAttributesType
): SizingCalculatorAttributesType {
  const validatedSizingAttributes = { ...sizingAttributes };

  Object.entries(validatedSizingAttributes).forEach(([type, items]) => {
    validatedSizingAttributes[type] = items.map((item: any) => {
      const revalidated: any = {
        ...item,
        valid: isValidSizingItem(
          type as keyof ValidSizingCalculatorAttributesType,
          item
        )
      };

      if (type === 'aboveGradeWalls') {
        revalidated.verticalGlass = revalidateNestedItems(
          item.verticalGlass,
          'verticalGlass'
        );
        revalidated.solidDoors = revalidateNestedItems(
          item.solidDoors,
          'solidDoors'
        );
      } else if (type === 'belowGradeWalls') {
        revalidated.verticalGlass = revalidateNestedItems(
          item.verticalGlass,
          'verticalGlass'
        );
        revalidated.solidDoors = revalidateNestedItems(
          item.solidDoors,
          'solidDoors'
        );
      } else if (
        type === 'ceilingsBelowAttic' ||
        type === 'ceilingsBelowRoof'
      ) {
        revalidated.skylights = revalidateNestedItems(
          item.skylights,
          'skylights'
        );
      }

      return revalidated;
    });
  });

  return validatedSizingAttributes;
}

export function confirmationInfoModal(messages: string[]): Promise<void> {
  if (messages.length === 0) return Promise.resolve();
  const htmlContent = `<div style="text-align: left;">
    <ul style="margin: 0; padding-left: 20px; color: #374151;">
      ${messages
        .map((msg) => `<li style="margin-bottom: 8px;">${msg}</li>`)
        .join('')}
    </ul>
  </div>`;
  return sweetalert
    .fire({
      title: 'Configuration note',
      html: htmlContent,
      icon: 'info',
      showCancelButton: false,
      confirmButtonText: 'OK',
      buttonsStyling: false,
      customClass: {
        confirmButton:
          'inline-flex items-center justify-center rounded-md px-3 text-sm font-medium leading-4 shadow-sm border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 border-transparent bg-blue-600 text-white hover:bg-blue-700 py-3 m-2'
      }
    })
    .then(() => undefined);
}

export function isErrorSeverity(severity: string): boolean {
  const s = severity.toLowerCase();
  return s === 'error' || s === 'critical';
}

export function formatValidationTitle(
  errorCount: number,
  warningCount: number
): string {
  const parts: string[] = [];

  if (errorCount > 0) {
    parts.push(`${errorCount} Error${errorCount > 1 ? 's' : ''}`);
  }

  if (warningCount > 0) {
    parts.push(`${warningCount} Warning${warningCount > 1 ? 's' : ''}`);
  }

  return parts.join(', ');
}
